// @flow

import queryString from 'query-string';
import React, { useState } from 'react';
import { chunk, isEmpty } from 'lodash';

import { AppIntegrationBrowser } from '@zapier/design-system-app-profiles';
import { Screen } from '@zapier/theme';
import { isBrowser } from '@zapier/common-utils';
import { useMediaProps } from '@zapier/common-components';

import ApolloQuery from 'web-ssr/app/common/components/ApolloQuery';
import appIntegrationBrowserQuery from 'web-ssr/app/app-directory/components/AppIntegrationBrowser/appIntegrationBrowserQuery';
import { createTwoAppPageLink } from 'web-ssr/app/app-directory/utils';

import type {
  appIntegrationBrowserQuery as appIntegrationBrowserQueryType,
  appIntegrationBrowserQueryVariables,
} from 'web-ssr/graphql/types';

type Result = {
  loading: *,
  fetchMore: *,
  error: *,
  data: appIntegrationBrowserQueryType,
};

type Props = {
  primaryAppSlug: string,
};

const pageSize = {
  small: 4,
  medium: 8,
  large: 9,
};

const initialPageIndex = isBrowser()
  ? queryString.parse(window.location.hash).page || 0
  : 0;

const AppIntegrationPromoContainer = (props: Props) => {
  const [pageIndex, setPageIndex] = useState(initialPageIndex);
  const { primaryAppSlug } = props;

  const layoutProps = useMediaProps({
    [Screen.queries.phone]: { size: 'small' },
    [Screen.queries.phablet]: { size: 'small' },
    [Screen.queries.tablet]: { size: 'medium' },
    [Screen.queries.desktop]: { size: 'large' },
  });

  const variables: appIntegrationBrowserQueryVariables = {
    slug: primaryAppSlug,
  };

  return (
    <ApolloQuery query={appIntegrationBrowserQuery} variables={variables}>
      {(result: Result) => {
        const { data, error } = result;

        const { appBySlug } = data;

        if (error || !appBySlug || !appBySlug.pairedApps) {
          return null;
        }

        const pages = chunk(appBySlug.pairedApps, pageSize[layoutProps.size]);

        // TODO: We should improve the handling of a zero-data state. In the
        // previous app directory, we would show the user a message stating
        // there is no data for this app.
        if (isEmpty(pages)) {
          return null;
        }

        const pageUrls = Array.from(
          { length: pages.length },
          (_v, i) => `#page=${i}`
        );

        const apps = pages[pageIndex].map(({ app, integrationsCount }) => {
          return {
            id: app.id,
            name: app.name,
            image: app.image || '', // TODO: Provide sane default
            categories: app.categories.map(cat => cat.name),
            integrationCount: integrationsCount,
            href: createTwoAppPageLink(appBySlug.slug, app.slug),
          };
        });

        return (
          <AppIntegrationBrowser
            apps={apps}
            initialPageIndex={pageIndex}
            onPaginate={paginateData => {
              setPageIndex(paginateData.pageIndex);
            }}
            pageUrls={pageUrls}
            size={layoutProps.size}
          />
        );
      }}
    </ApolloQuery>
  );
};

export default AppIntegrationPromoContainer;
